import { useCallback } from 'react';
import { metricsApi } from '../../../../../shared/api/metrics';
import { filterOptionsApi } from '../../../../../shared/components/Filters/api';
import type { MetricsFilters } from '../../../../../shared/api/types';

interface BreakdownEntry {
  label: string;
  value: number | null;
  rag_label?: string | null;
}

export type WellnessBreakdowns = Record<string, BreakdownEntry[]>;

const METRIC_KEYS = ['recovery_score', 'strain', 'sleep_performance', 'average_hrv'];
const METRIC_LABELS = ['Recovery', 'Strain', 'Sleep performance', 'HRV'];

export const useWellnessBreakdowns = (baseFilterParams: MetricsFilters, clientId: string | null | undefined) => {
  const fetchBreakdowns = useCallback(async (): Promise<WellnessBreakdowns> => {
    if (!clientId) return {};

    const breakdowns: WellnessBreakdowns = {};

    try {
      const regions = await filterOptionsApi.getRegions(clientId);
      if (regions.data?.length) {
        const regionStats = await Promise.all(
          regions.data.map((r) =>
            metricsApi.getStats({ ...baseFilterParams, region: [r.id], metrics: METRIC_KEYS }).catch(() => null),
          ),
        );
        METRIC_KEYS.forEach((key, i) => {
          breakdowns[`${METRIC_LABELS[i]} by region`] = regions.data.map((r, ri) => ({
            label: r.name,
            value: regionStats[ri]?.data?.[key]?.value ?? null,
            rag_label: regionStats[ri]?.data?.[key]?.rag_label ?? null,
          }));
        });
      }
    } catch {
      // Region data unavailable
    }

    try {
      const departments = await filterOptionsApi.getDepartments(clientId, false, false, []);
      if (departments.data?.length) {
        const deptStats = await Promise.all(
          departments.data.map((d) =>
            metricsApi.getStats({ ...baseFilterParams, department: [d.id], metrics: METRIC_KEYS }).catch(() => null),
          ),
        );
        METRIC_KEYS.forEach((key, i) => {
          breakdowns[`${METRIC_LABELS[i]} by department`] = departments.data.map((d, di) => ({
            label: d.name,
            value: deptStats[di]?.data?.[key]?.value ?? null,
            rag_label: deptStats[di]?.data?.[key]?.rag_label ?? null,
          }));
        });
      }
    } catch {
      // Department data unavailable
    }

    return breakdowns;
  }, [baseFilterParams, clientId]);

  return { fetchBreakdowns };
};
